import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Archive, Calendar, Users } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "ml" ? "ml-IN" : "en-IN", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

export const PreviousPolls = () => {
  const { t, lang } = useLang();
  const [results, setResults] = useState(null);
  const [pollState, setPollState] = useState(null);

  useEffect(() => {
    axios.get(`${API}/results`).then((r) => setResults(r.data)).catch(() => {});
    axios.get(`${API}/poll-state`).then((r) => setPollState(r.data)).catch(() => {});
  }, []);

  const totalVotes = results?.total_votes ?? null;

  const prevSchema = [
    {
      "@type": "CollectionPage",
      "url": "https://thenextcm.com/previous-polls",
      "name": "Previous Polls — Kerala Speaks",
      "description": "Archive of completed Kerala civic polls and citizen participation moments.",
      "inLanguage": ["en", "ml"],
      "isPartOf": { "@id": "https://thenextcm.com/#website" }
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thenextcm.com/" },
        { "@type": "ListItem", "position": 2, "name": "Previous Polls", "item": "https://thenextcm.com/previous-polls" }
      ]
    }
  ];

  return (
    <div className="App min-h-screen" data-testid="previous-polls-page">
      <SeoHead
        title={lang === "ml" ? "മുൻ പോളുകൾ — കേരളം പറയുന്നു" : "Previous Polls — Kerala Speaks"}
        description={lang === "ml"
          ? "പൂർത്തിയായ പൗര നിമിഷങ്ങളുടെ ആർക്കൈവ് — കേരളം മുഖ്യമന്ത്രി 2026 പോൾ ഉൾപ്പെടെ."
          : "Archive of completed Kerala civic polls — including the Kerala CM 2026 citizens' poll with 500,000+ votes."}
        canonical="/previous-polls"
        schema={prevSchema}
      />
      <section className="max-w-5xl mx-auto px-5 sm:px-6 md:px-12 pt-10 md:pt-16 pb-20">
        <p
          className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] text-neutral-600 mb-5"
          data-testid="prev-polls-eyebrow"
        >
          ★ {t("prev_polls_eyebrow")} ★
        </p>
        <h1
          className="font-display uppercase leading-[0.85] tracking-tighter"
          style={{ fontSize: "clamp(2.5rem, 7vw, 5rem)" }}
          data-testid="prev-polls-title"
        >
          {t("prev_polls_title")}
        </h1>
        <p className="mt-5 max-w-2xl text-base md:text-lg font-medium leading-relaxed">
          {t("prev_polls_sub")}
        </p>

        <div className="mt-10 space-y-6" data-testid="prev-polls-list">
          {/* Archive card: Kerala CM 2026 */}
          <Link
            to="/previous-polls/kerala-cm-2026"
            className="brutal-card block p-5 md:p-7 hover:translate-x-[2px] hover:-translate-y-[2px] transition-transform"
            data-testid="archive-card-kerala-cm-2026"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div
                className="border-4 border-black flex items-center justify-center shrink-0"
                style={{ background: "#E3000F", color: "#FFFFFF", width: 72, height: 72 }}
                aria-hidden="true"
              >
                <Archive size={32} strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
                  {t("archive_card_label")}
                </p>
                <h2
                  className="font-display uppercase leading-none mt-2"
                  style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.25rem)" }}
                >
                  {t("archive_card_title")}
                </h2>
                <p className="text-sm md:text-base font-medium mt-2 text-neutral-700">
                  {t("archive_card_sub")}
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-xs uppercase font-bold tracking-[0.25em] text-neutral-600">
                  <span className="flex items-center gap-1.5" data-testid="archive-stat-votes">
                    <Users size={14} strokeWidth={3} />
                    {totalVotes != null ? totalVotes.toLocaleString() : "—"} {t("votes_word")}
                  </span>
                  <span className="flex items-center gap-1.5" data-testid="archive-stat-date">
                    <Calendar size={14} strokeWidth={3} />
                    {t("archive_card_announced_on")}: {formatDate(pollState?.announced_at, lang)}
                  </span>
                  {pollState?.announced_cm_name && (
                    <span className="flex items-center gap-1.5" data-testid="archive-stat-cm">
                      ★ {pollState.announced_cm_name}
                    </span>
                  )}
                </div>
              </div>
              <span
                className="brutal-btn shrink-0"
                style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }}
              >
                {t("archive_view_btn")} →
              </span>
            </div>
          </Link>

          {/* Placeholder for future polls */}
          <div
            className="brutal-card p-5 md:p-7 opacity-60"
            data-testid="archive-card-coming-soon"
          >
            <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
              {t("archive_coming_soon_label")}
            </p>
            <p
              className="font-display uppercase leading-none mt-2"
              style={{ fontSize: "clamp(1.25rem, 3vw, 1.75rem)" }}
            >
              {t("archive_coming_soon_title")}
            </p>
            <p className="text-sm font-medium mt-2 text-neutral-700">
              {t("archive_coming_soon_sub")}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PreviousPolls;
